import { generateDeliveryteamOrderInfoId } from "./generateCartId";
import { KNOWN_ERROR } from '../types/errors';
import type { TaxSettings } from "@lib/zod/taxSchema";
import { isOpenNow } from '@lib/utils/isOpenNow';
import { listAvailablePaymentMethods } from '@lib/utils/listAvailablePaymentMethods';
import type { ResType } from "@api-client/typeUtils";
import { type zmgPublicCacheClient } from "@api-client/zmg_cache";
import type { AddressType } from "@lib/zod/userAddressSchema";
import type { DeliveryteamOrderInfoType } from "@lib/zod/cartItemState";

type PaymentMethodsDataType = ResType<typeof zmgPublicCacheClient.deliveryteam.payment_methods[':id']['data.json']['$get']>["data"];
type OpenHours = ResType<typeof zmgPublicCacheClient.deliveryteam.open_hours[':id']['data.json']['$get']>["data"]

type userDataType = Awaited<ReturnType<typeof import("@db/services/user_data.service").getUserData_Service>>;

const ONE_WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000;

export const checkBeforePlacingDeliveryteamOrder = (
    userData: userDataType,
    websiteId: string,
    deliveryteamId: string,
    selectedPaymentMethodId: string,
) => {
    if (!userData) {
        throw new KNOWN_ERROR("User data not found", "USER_DATA_NOT_FOUND");
    }
    if (!selectedPaymentMethodId) {
        throw new KNOWN_ERROR("Payment method not selected", "PAYMENT_METHOD_NOT_SELECTED");
    }
    // if (!userData.rememberLastOrderType) {
    //     throw new KNOWN_ERROR("Order type not found", "ORDER_TYPE_NOT_FOUND");
    // }
    const cartId = generateDeliveryteamOrderInfoId(websiteId, deliveryteamId);
    const currentDeliveryteamInfo = userData.deliveryteamOrderInfos?.[cartId];
    if (!currentDeliveryteamInfo) {
        throw new KNOWN_ERROR("Courier order info not found", "DELIVERYTEAM_ORDER_INFO_NOT_FOUND");
    }
    if (currentDeliveryteamInfo.scheduled_at_ts) {
        // it can not be more than 1 week
        if (currentDeliveryteamInfo.scheduled_at_ts > Date.now() + ONE_WEEK_IN_MS) {
            throw new KNOWN_ERROR("Scheduled order can not be more than 1 week", "SCHEDULED_ORDER_CAN_NOT_BE_MORE_THAN_1_WEEK");
        }
    }

    if (!userData.deliveryteamPickUpAddressId) {
        throw new KNOWN_ERROR("Pickup address not found", "PICKUP_ADDRESS_NOT_FOUND");
    }
    const pickupAddress = userData.addresses?.[userData.deliveryteamPickUpAddressId];
    if (!pickupAddress) {
        throw new KNOWN_ERROR("Pickup address not found", "PICKUP_ADDRESS_NOT_FOUND");
    }
    if (!pickupAddress?.lat && !pickupAddress?.lng) {
        throw new KNOWN_ERROR("Pickup address coordinates are required", "PICKUP_ADDRESS_COORDINATES_REQUIRED");
    }
    if (!pickupAddress?.address1) {
        throw new KNOWN_ERROR("Pickup address1 is required", "PICKUP_ADDRESS1_REQUIRED");
    }
    if (!pickupAddress?.country) {
        throw new KNOWN_ERROR("Pickup address country is required", "PICKUP_ADDRESS_COUNTRY_REQUIRED");
    }

    if (!userData.deliveryteamDeliveryAddressId) {
        throw new KNOWN_ERROR("Delivery address not found", "DELIVERY_ADDRESS_NOT_FOUND");
    }
    const deliveryAddress = userData.addresses?.[userData.deliveryteamDeliveryAddressId];
    if (!deliveryAddress) {
        throw new KNOWN_ERROR("Delivery address not found", "DELIVERY_ADDRESS_NOT_FOUND");
    }
    if (!deliveryAddress?.address1) {
        throw new KNOWN_ERROR("Delivery address1 is required", "DELIVERY_ADDRESS1_REQUIRED");
    }
    if (!deliveryAddress?.country) {
        throw new KNOWN_ERROR("Delivery address country is required", "DELIVERY_ADDRESS_COUNTRY_REQUIRED");
    }
    if (!deliveryAddress?.lat && !deliveryAddress?.lng) {
        throw new KNOWN_ERROR("Delivery address coordinates are required", "DELIVERY_ADDRESS_COORDINATES_REQUIRED");
    }
    return {
        currentCart: currentDeliveryteamInfo,
        pickupAddress,
        deliveryAddress,
    };
}

/**
 * Same rules as {@link checkBeforePlacingDeliveryteamOrder} for addresses, cart and schedule,
 * but for a caller that already has `currentCart` and addresses (e.g. store dashboard / API body).
 */
export const checkExplicitDeliveryteamOrderPayload = (
    currentCart: DeliveryteamOrderInfoType | null | undefined,
    pickupAddress: AddressType | null | undefined,
    deliveryAddress: AddressType | null | undefined,
    selectedPaymentMethodId: string | undefined,
) => {
    if (!selectedPaymentMethodId) {
        throw new KNOWN_ERROR("Payment method not selected", "PAYMENT_METHOD_NOT_SELECTED");
    }
    if (!currentCart) {
        throw new KNOWN_ERROR("Courier order info not found", "DELIVERYTEAM_ORDER_INFO_NOT_FOUND");
    }
    if (!currentCart.deliveryteamId) {
        throw new KNOWN_ERROR("Courier order info not found", "DELIVERYTEAM_ORDER_INFO_NOT_FOUND");
    }
    if (currentCart.scheduled_at_ts) {
        if (currentCart.scheduled_at_ts > Date.now() + ONE_WEEK_IN_MS) {
            throw new KNOWN_ERROR("Scheduled order can not be more than 1 week", "SCHEDULED_ORDER_CAN_NOT_BE_MORE_THAN_1_WEEK");
        }
    }

    if (!pickupAddress) {
        throw new KNOWN_ERROR("Pickup address not found", "PICKUP_ADDRESS_NOT_FOUND");
    }
    if (!pickupAddress?.lat && !pickupAddress?.lng) {
        throw new KNOWN_ERROR("Pickup address coordinates are required", "PICKUP_ADDRESS_COORDINATES_REQUIRED");
    }
    if (!pickupAddress?.address1) {
        throw new KNOWN_ERROR("Pickup address1 is required", "PICKUP_ADDRESS1_REQUIRED");
    }
    if (!pickupAddress?.country) {
        throw new KNOWN_ERROR("Pickup address country is required", "PICKUP_ADDRESS_COUNTRY_REQUIRED");
    }

    if (!deliveryAddress) {
        throw new KNOWN_ERROR("Delivery address not found", "DELIVERY_ADDRESS_NOT_FOUND");
    }
    if (!deliveryAddress?.address1) {
        throw new KNOWN_ERROR("Delivery address1 is required", "DELIVERY_ADDRESS1_REQUIRED");
    }
    if (!deliveryAddress?.country) {
        throw new KNOWN_ERROR("Delivery address country is required", "DELIVERY_ADDRESS_COUNTRY_REQUIRED");
    }
    if (!deliveryAddress?.lat && !deliveryAddress?.lng) {
        throw new KNOWN_ERROR("Delivery address coordinates are required", "DELIVERY_ADDRESS_COORDINATES_REQUIRED");
    }
    return {
        currentCart,
        pickupAddress,
        deliveryAddress,
    };
};


export const checkDeliveryteamDataBeforePlacingOrder = (
    deliveryteamBaseData: {
        name: string;
        isActive: boolean;
    } | undefined,
    deliveryteamStatusData: {
        isSuspended: boolean;
    } | undefined,
    paymentMethodsData: PaymentMethodsDataType,
    taxSettings: TaxSettings,
    openHours: OpenHours,
    selectedPaymentMethodId: string,
    scheduled_at_ts?: number,
    hasNegativeBalance: boolean = false,
) => {
    if (deliveryteamStatusData?.isSuspended) {
        throw new KNOWN_ERROR("Courier is suspended", "COURIER_IS_SUSPENDED");
    }
    if (!deliveryteamBaseData) {
        throw new KNOWN_ERROR("Courier data not found", "COURIER_DATA_NOT_FOUND");
    }
    if (!deliveryteamBaseData.isActive) {
        throw new KNOWN_ERROR("Courier is not active", "COURIER_IS_NOT_ACTIVE");
    }
    const isOpen = isOpenNow(openHours?.timezone ?? null, openHours?.openHours ?? null, scheduled_at_ts);
    if (
        !isOpen
        // What about scheduled orders? we do not have scheduled orders yet. maybe later
    ) {
        throw new KNOWN_ERROR("Courier is not in operating hours", "COURIER_IS_NOT_IN_OPERATING_HOURS");
    }
    if (!taxSettings) {
        throw new KNOWN_ERROR("Tax settings not found", "TAX_SETTINGS_NOT_FOUND");
    }

    const availablePaymentMethods = listAvailablePaymentMethods(paymentMethodsData, 'ignored-order-type', hasNegativeBalance);
    if (!availablePaymentMethods || availablePaymentMethods.length === 0) {
        throw new KNOWN_ERROR("Payment methods not configured for courier", "PAYMENT_METHODS_NOT_CONFIGURED");
    }
    const currentPaymentMethod = availablePaymentMethods.find(method => method.id === selectedPaymentMethodId);
    if (!currentPaymentMethod) {
        throw new KNOWN_ERROR("Invalid payment method", "INVALID_PAYMENT_METHOD");
    }
    return {
        taxSettings,
        currentPaymentMethod,
        deliveryteamTimezone: openHours?.timezone,
    };
}
